import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { BadgeTone } from '@/components/ui/badge'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'

export type CustomerCategory = { id: string; name: string; color: BadgeTone; sortOrder: number; isActive: boolean; inUse: number }

const KEY = ['crm', 'categories'] as const
const TONES: BadgeTone[] = ['neutral', 'primary', 'success', 'warning', 'danger', 'info']

export const useCategories = () =>
  useQuery({
    queryKey: KEY,
    staleTime: 5 * 60_000,
    queryFn: async () =>
      asList(await api.get<Record<string, unknown>[]>('/api/v1/crm/categories')).map(
        (r): CustomerCategory => ({
          id: String(r.id ?? ''),
          name: String(r.name ?? ''),
          color: TONES.includes(r.color as BadgeTone) ? (r.color as BadgeTone) : 'neutral',
          sortOrder: Number(r.sort_order) || 0,
          isActive: r.is_active !== false,
          inUse: Number(r.in_use) || 0,
        }),
      ),
  })

export function useCategoryAdmin() {
  const qc = useQueryClient()
  const onSuccess = () => qc.invalidateQueries({ queryKey: KEY })
  const save = useMutation({
    mutationFn: ({ id, input }: { id?: string; input: { name: string; color: string; sort_order: number; is_active: boolean } }) =>
      id ? api.put(`/api/v1/crm/categories/${id}`, input) : api.post('/api/v1/crm/categories', input),
    onSuccess,
  })
  const remove = useMutation({ mutationFn: (id: string) => api.del<{ deleted?: boolean }>(`/api/v1/crm/categories/${id}`), onSuccess })
  return { save, remove, tones: TONES }
}
